import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Fetch financial establishment list for dropdown
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFeBussinessPremMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    financial_establishment: '',
    name: '',
    address: '',
    contact_number: '',
    email: '',
    website: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'name', field: 'name', headerName: 'Name', width: 180 },
    { id: 'address', field: 'address', headerName: 'Address', width: 300 },
    { id: 'contact_number', field: 'contact_number', headerName: 'Contact Number', width: 180 },
    { id: 'email', field: 'email', headerName: 'Email', width: 200 },
    { id: 'website', field: 'website', headerName: 'Website', width: 200 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const fetchBusinessPremises = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-bussiness-prem/`)
      .then(response => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            name: item.name,
            address: item.address,
            contact_number: item.contact_number,
            email: item.email,
            website: item.website,
          };
        });
        setRows(apiData);
        setLoading(false);
      }).catch(error => {
        console.error('Failed to fetch Business Premises:', error);
        setSnackbarMessage('Failed to load Business Premises.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) {
      fetchBusinessPremises();
    }
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddItem = () => {
    setIsEditMode(false);
    setFormData({
      financial_establishment: '',
      name: '',
      address: '',
      contact_number: '',
      email: '',
      website: '',
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);
    setFormData({
      financial_establishment: feBaseObj ? feBaseObj.id : '',
      name: item.name,
      address: item.address,
      contact_number: item.contact_number,
      email: item.email,
      website: item.website,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fe-bussiness-prem/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Business Premises deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchBusinessPremises();
      }).catch(error => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete Business Premises.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }).finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.financial_establishment) errors.financial_establishment = 'FE Base is required';
    if (!formData.name?.trim()) errors.name = 'Name is required';
    if (!formData.address?.trim()) errors.address = 'Address is required';
    if (!formData.contact_number?.trim()) errors.contact_number = 'Contact number is required';
    if (!formData.email?.trim()) errors.email = 'Email is required';
    if (!formData.website?.trim()) errors.website = 'Website is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      financial_establishment: formData.financial_establishment,
      name: formData.name,
      address: formData.address,
      contact_number: formData.contact_number,
      email: formData.email,
      website: formData.website,
    };
    try {
      setUploading(true);
      if(isEditMode) {
        await api.patch(`/fe-mgmt/fe-bussiness-prem/${selectedItem.id}/`, payload);
        setSnackbarMessage('Business Premises updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-bussiness-prem/`, payload);
        setSnackbarMessage('Business Premises added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchBusinessPremises();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit Business Premises.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Business Premises
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>
          Add
        </Button>
      </Box>
      <TableComponent 
        columns={columns} 
        rows={rows} 
        manageColumnStatus 
        enableSort 
        sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }}
      />
      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{ isEditMode ? 'Edit Business Premises' : 'Add Business Premises' }</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="FE Base"
            fullWidth
            value={formData.financial_establishment}
            error={!!formErrors.financial_establishment}
            helperText={formErrors.financial_establishment}
            onChange={e => setFormData({...formData, financial_establishment: e.target.value})}
            disabled={uploading}
          >
            {feBaseList.map(fe => (
              <MenuItem key={fe.id} value={fe.id}>
                {fe.name_of_fe || fe.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={formData.name}
            error={!!formErrors.name}
            helperText={formErrors.name}
            onChange={e => setFormData({...formData, name: e.target.value})}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Address"
            fullWidth
            multiline
            minRows={3}
            value={formData.address}
            error={!!formErrors.address}
            helperText={formErrors.address}
            onChange={e => setFormData({...formData, address: e.target.value})}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Contact Number"
            fullWidth
            value={formData.contact_number}
            error={!!formErrors.contact_number}
            helperText={formErrors.contact_number}
            onChange={e => setFormData({...formData, contact_number: e.target.value})}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Email"
            fullWidth
            type="email"
            value={formData.email}
            error={!!formErrors.email}
            helperText={formErrors.email}
            onChange={e => setFormData({...formData, email: e.target.value})}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Website"
            fullWidth
            value={formData.website}
            error={!!formErrors.website}
            helperText={formErrors.website}
            onChange={e => setFormData({...formData, website: e.target.value})}
            disabled={uploading}
          />
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                Processing...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this Business Premises?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};
